import React, { useState, useEffect, useContext } from 'react';
import { AppContext } from '../context/AppContext';
import { useTranslation } from 'react-i18next';
import DataTable from '../components/DataTable';
import Modal from '../components/Modal';

const Departments = () => {
    const { authFetch, user } = useContext(AppContext);
    const { t } = useTranslation();
    const isAdmin = user?.role === 'Admin' || user?.role === 'Manager';

    const [departments, setDepartments] = useState([]);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [form, setForm] = useState({ name: '' });

    const fetchDepartments = () => {
        authFetch('/api/departments').then(r => r.json()).then(d => setDepartments(Array.isArray(d) ? d : []));
    };

    useEffect(() => { fetchDepartments(); }, []);

    const handleAdd = async (e) => {
        e.preventDefault();
        const res = await authFetch('/api/departments', { method: 'POST', body: JSON.stringify(form) });
        if (res.ok) { setIsAddModalOpen(false); fetchDepartments(); setForm({ name: '' }); }
        else alert(t('Error adding department'));
    };

    const handleEdit = async (e) => {
        e.preventDefault();
        const res = await authFetch(`/api/departments/${selectedId}`, { method: 'PUT', body: JSON.stringify(form) });
        if (res.ok) { setIsEditModalOpen(false); fetchDepartments(); }
        else alert(t('Error updating department'));
    };

    const handleDelete = async (id) => {
        if (!window.confirm(t('Are you sure you want to delete this department?'))) return;
        const res = await authFetch(`/api/departments/${id}`, { method: 'DELETE' });
        if (res.ok) fetchDepartments();
        else alert(t('Delete failed - Admin only'));
    };

    const columns = [
        { header: t('ID'), accessor: 'id' },
        { 
            header: t('Department Name'), accessor: 'name',
            render: r => <span className="font-bold text-gray-800">{r.name}</span>
        },
        { 
            header: t('Employees Count'), accessor: 'employee_count',
            render: r => <span className="bg-indigo-100 text-indigo-800 px-2 py-1 rounded-full text-xs font-bold">{r.employee_count} {t('Employees')}</span>
        },
        { 
            header: t('Created At'), accessor: 'created_at',
            render: r => new Date(r.created_at).toLocaleDateString()
        },
        {
            header: t('Actions'), accessor: 'actions',
            render: r => (
                <div className="flex gap-2">
                    {isAdmin && (
                        <>
                            <button className="px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 text-sm font-bold" onClick={() => {
                                setSelectedId(r.id);
                                setForm({ name: r.name });
                                setIsEditModalOpen(true);
                            }}>{t('Edit')}</button>
                            <button className="px-3 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 text-sm font-bold" onClick={() => handleDelete(r.id)}>{t('Delete')}</button>
                        </>
                    )}
                </div>
            )
        }
    ];

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-3xl font-bold text-gray-800">{t('Departments')}</h2>
                    <p className="text-gray-500 text-sm mt-1">{t('Manage company departments and classifications')}</p>
                </div>
                {isAdmin && (
                    <button onClick={() => { setForm({ name: '' }); setIsAddModalOpen(true); }} className="bg-indigo-600 text-white px-4 py-2 rounded shadow hover:bg-indigo-700 transition font-bold">
                        + {t('Add Department')}
                    </button>
                )}
            </div>

            <DataTable columns={columns} data={departments} />

            <Modal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} title={t('Add Department')}>
                <form onSubmit={handleAdd} className="space-y-4">
                    <div>
                        <label className="block text-sm font-bold text-gray-700">{t('Department Name')}</label>
                        <input required className="w-full border p-2 rounded mt-1" value={form.name} onChange={e => setForm({ name: e.target.value })} />
                    </div>
                    <button type="submit" className="w-full bg-indigo-600 text-white py-2 rounded font-bold hover:bg-indigo-700">{t('Save')}</button>
                </form>
            </Modal>

            <Modal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} title={t('Edit Department')}>
                <form onSubmit={handleEdit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-bold text-gray-700">{t('Department Name')}</label>
                        <input required className="w-full border p-2 rounded mt-1" value={form.name} onChange={e => setForm({ name: e.target.value })} />
                    </div>
                    <button type="submit" className="w-full bg-blue-600 text-white py-2 rounded font-bold hover:bg-blue-700">{t('Update')}</button>
                </form>
            </Modal>
        </div>
    );
};

export default Departments;
